"use client";

import { useState, useEffect } from "react";

export type BusinessType = "RESTAURANT" | "CAFE" | "BAR" | "FOOD_TRUCK" | "JEWELRY";

interface UseBusinessTypeReturn {
  businessType: BusinessType;
  loading: boolean;
  error: string | null;
  isRestaurant: boolean;
  isCafe: boolean;
  isBar: boolean;
  isFoodTruck: boolean;
  isJewelry: boolean;
  isFullService: boolean;
  isQuickService: boolean;
  refetch: () => Promise<void>;
}

export function useBusinessType(): UseBusinessTypeReturn {
  const [businessType, setBusinessType] = useState<BusinessType>("RESTAURANT");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBusinessType = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/organizations");
      if (!res.ok) throw new Error("Failed to fetch organization");
      const data = await res.json();
      const org = data.organizations?.[0];
      if (org?.businessType) {
        setBusinessType(org.businessType as BusinessType);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessType();
  }, []);

  const isRestaurant = businessType === "RESTAURANT";
  const isCafe = businessType === "CAFE";
  const isBar = businessType === "BAR";
  const isFoodTruck = businessType === "FOOD_TRUCK";
  const isJewelry = businessType === "JEWELRY";

  // Full service = needs tables, kitchen display, seat numbers
  const isFullService = isRestaurant || isBar;
  // Quick service = no table management, direct checkout
  const isQuickService = isCafe || isFoodTruck;

  return {
    businessType,
    loading,
    error,
    isRestaurant,
    isCafe,
    isBar,
    isFoodTruck,
    isJewelry,
    isFullService,
    isQuickService,
    refetch: fetchBusinessType,
  };
}
